/**
 * Level10Scene - 关卡10：电磁挑战
 * @description 综合运用电磁物理知识：电场力、磁场力与洛伦兹力
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import { EMField } from '@/physics/EMField.ts';
import { FieldRenderer } from '@/physics/FieldRenderer.ts';
import type { LevelConfig, CheckpointConfig, StageConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';
import type { ChargedParticle } from '@/physics/EMField.ts';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

type GameState = 'idle' | 'running' | 'success' | 'failed';
type CurrentStage = 1 | 2 | 3;

const STAGE_COLORS: Record<CurrentStage, number> = {
  1: 0xff6b6b, // 红色 - 电场阶段
  2: 0x9b59b6, // 紫色 - 磁场阶段
  3: 0x3498db, // 蓝色 - 电磁感应阶段
};

const CHECKPOINT_COLOR = 0xffa500;
const GOAL_COLOR = 0x4ade80;

interface EMLevelConfig extends LevelConfig {
  chargeSources?: Array<{
    id: string;
    position: { x: number; y: number };
    charge: number;
  }>;
  magneticSources?: Array<{
    id: string;
    position: { x: number; y: number };
    strength: number;
    radius?: number;
  }>;
}

export class Level10Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private emField!: EMField;
  private fieldRenderer!: FieldRenderer;
  private levelConfig: EMLevelConfig | null = null;
  private gameState: GameState = 'idle';
  private currentStage: CurrentStage = 1;
  private checkpoints: CheckpointConfig[] = [];
  private stages: StageConfig[] = [];
  private completedStages: Set<number> = new Set();
  private checkpointGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private goalZone!: Phaser.Geom.Rectangle;
  private isVictory: boolean = false;
  private stageText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private formulaText!: Phaser.GameObjects.Text;
  private elapsedTime: number = 0;

  // 带电粒子属性
  private playerCharge: number = 1.0;
  private playerMass: number = 1.0;

  // 粒子轨迹记录
  private trajectoryPoints: Array<{ x: number; y: number }> = [];
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'Level10Scene' });
  }

  preload(): void {
    this.load.json('level-10', 'src/levels/levels/level-10.json');
  }

  create(): void {
    this.levelConfig = this.cache.json.get('level-10') as EMLevelConfig;

    if (this.levelConfig) {
      this.checkpoints = this.levelConfig.checkpoints ?? [];
      this.stages = this.levelConfig.stages ?? [];
      const levelSpecific = this.levelConfig.levelSpecific as { playerCharge?: number; playerMass?: number } | undefined;
      this.playerCharge = levelSpecific?.playerCharge ?? 1.0;
      this.playerMass = levelSpecific?.playerMass ?? 1.0;
    }

    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(
        this.levelConfig?.physics.gravity.x ?? 0,
        this.levelConfig?.physics.gravity.y ?? -5
      ),
      allowSleep: this.levelConfig?.physics.allowSleep ?? true,
    });

    // 初始化电磁场
    this.emField = new EMField(1e-4);
    this.setupEMFieldSources();

    // 初始化场线渲染器
    this.fieldRenderer = new FieldRenderer(
      this,
      this.emField,
      {
        electricFieldColor: 0xff6b6b,
        electricFieldColorNegative: 0x4ecdc4,
        magneticFieldColor: 0x9b59b6,
        lineWidth: 2,
        fieldLineCount: 8,
        startRadius: 25,
        maxLineLength: 300,
      },
      GAME_HEIGHT,
      1
    );

    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(4);

    this.createBackground();
    this.createBoundaries();
    this.createPlatforms();
    this.createEMZones();
    this.createCheckpoints();
    this.createGoalZone();
    this.createStickman();
    this.createUI();
    this.setupInput();

    // 初始渲染电磁场
    this.fieldRenderer.render();

    this.gameState = 'running';
    this.currentStage = 1;
    this.completedStages = new Set();
    this.isVictory = false;
    this.elapsedTime = 0;
    this.trajectoryPoints = [];
  }

  /**
   * 设置电磁场源
   */
  private setupEMFieldSources(): void {
    // 清除旧源
    this.emField.clear();

    // 添加电荷源
    const chargeSources = this.levelConfig?.chargeSources ?? [];
    for (const source of chargeSources) {
      this.emField.addChargeSource({
        id: source.id,
        position: Vec2(source.position.x, source.position.y),
        charge: source.charge,
      });
    }

    // 添加磁场源
    const magneticSources = this.levelConfig?.magneticSources ?? [];
    for (const source of magneticSources) {
      this.emField.addMagneticSource({
        id: source.id,
        position: Vec2(source.position.x, source.position.y),
        strength: source.strength,
        radius: source.radius,
      });
    }
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running' || this.isVictory) return;

    const dt = delta / 1000;
    this.elapsedTime += dt;

    // 物理步进
    this.physicsWorld.step(dt);

    // 应用洛伦兹力到玩家
    this.applyLorentzForceToPlayer(dt);

    // 更新火柴人渲染
    this.stickman.update();

    // 更新粒子轨迹
    this.updateTrajectory();

    // 检查检查点碰撞
    this.checkCheckpointCollisions();

    // 检查胜利条件
    this.checkVictory();

    // 更新阶段显示
    this.updateStageDisplay();

    // 定期重新渲染场线（每500ms）
    if (Math.floor(this.elapsedTime * 2) % 1 === 0) {
      this.fieldRenderer.render();
    }
  }

  /**
   * 应用洛伦兹力到玩家
   */
  private applyLorentzForceToPlayer(dt: number): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (!torso) return;

    const position = torso.getPosition();
    const velocity = torso.getLinearVelocity();

    // 创建带电粒子配置
    const particle: ChargedParticle = {
      id: 'player',
      position: Vec2(position.x, position.y),
      velocity: Vec2(velocity.x, velocity.y),
      charge: this.playerCharge,
      mass: this.playerMass,
    };

    // 计算洛伦兹力
    const lorentzForce = this.emField.calculateLorentzForce(particle);

    // 应用力到物理体（缩放以适应游戏）
    const forceScale = 50;
    torso.applyForceToCenter(
      Vec2(lorentzForce.x * forceScale, lorentzForce.y * forceScale)
    );

    // 根据当前阶段动态调整场源
    this.updateDynamicFields(dt);
  }

  /**
   * 动态更新电磁场（阶段3：电磁感应）
   */
  private updateDynamicFields(_dt: number): void {
    if (this.currentStage !== 3) return;

    // 阶段3：变化磁场产生感应电场
    // 周期性改变磁场强度
    const time = this.elapsedTime;
    const oscillation = Math.sin(time * 2) * 0.5 + 1;

    // 更新磁场源强度
    const magneticSources = this.emField.getAllMagneticSources();
    for (const source of magneticSources) {
      if (source.id === 'magnetic-north') {
        this.emField.removeMagneticSource(source.id);
        this.emField.addMagneticSource({
          ...source,
          strength: source.strength * oscillation,
        });
      }
    }
  }

  /**
   * 更新粒子轨迹
   */
  private updateTrajectory(): void {
    const headPos = this.stickman.getHeadPosition();

    // 添加新点
    this.trajectoryPoints.push({ x: headPos.x, y: headPos.y });

    // 限制轨迹长度
    if (this.trajectoryPoints.length > 100) {
      this.trajectoryPoints.shift();
    }

    // 绘制轨迹
    this.trajectoryGraphics.clear();
    this.trajectoryGraphics.lineStyle(2, 0x00ff00, 0.5);

    if (this.trajectoryPoints.length > 1) {
      const firstPoint = this.trajectoryPoints[0];
      if (firstPoint) {
        this.trajectoryGraphics.beginPath();
        this.trajectoryGraphics.moveTo(firstPoint.x, firstPoint.y);
      }

      for (let i = 1; i < this.trajectoryPoints.length; i++) {
        const point = this.trajectoryPoints[i];
        if (point) {
          this.trajectoryGraphics.lineTo(point.x, point.y);
        }
      }

      this.trajectoryGraphics.strokePath();
    }
  }

  private createBackground(): void {
    this.cameras.main.setBackgroundColor('#0a0a1e');

    // 电磁主题网格
    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2a2a4a, 0.2);
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, GAME_HEIGHT);
      grid.strokePath();
    }
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(GAME_WIDTH, y);
      grid.strokePath();
    }

    // 添加电磁波纹效果
    this.createEMWaveEffect();
  }

  /**
   * 创建电磁波纹视觉效果
   */
  private createEMWaveEffect(): void {
    const waveGraphics = this.add.graphics();
    waveGraphics.setDepth(1);

    // 定期更新波纹
    this.time.addEvent({
      delay: 100,
      callback: () => {
        waveGraphics.clear();
        const chargeSources = this.emField.getAllChargeSources();

        for (const source of chargeSources) {
          const screenPos = this.toScreenCoords(source.position);
          const radius = 30 + Math.sin(this.elapsedTime * 3) * 10;
          const alpha = 0.3 + Math.sin(this.elapsedTime * 5) * 0.1;

          waveGraphics.lineStyle(2, source.charge > 0 ? 0xff6b6b : 0x4ecdc4, alpha);
          waveGraphics.strokeCircle(screenPos.x, screenPos.y, radius);
          waveGraphics.strokeCircle(screenPos.x, screenPos.y, radius + 15);
        }
      },
      loop: true,
    });
  }

  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private createPlatforms(): void {
    const platforms = this.levelConfig?.platforms ?? [];
    const platformGraphics = this.add.graphics();

    for (const platform of platforms) {
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction: platform.friction ?? 0.5,
        restitution: platform.restitution ?? 0,
      });

      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      const color = platform.color ?? 0x4a4a6a;

      platformGraphics.fillStyle(color, 1);
      platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
    }
  }

  /**
   * 创建电磁场区域可视化
   */
  private createEMZones(): void {
    const zoneGraphics = this.add.graphics();
    zoneGraphics.setDepth(2);

    // 电场区域标记
    const chargeSources = this.levelConfig?.chargeSources ?? [];
    for (const source of chargeSources) {
      const screenPos = this.toScreenCoords({ x: source.position.x, y: source.position.y });
      const color = source.charge > 0 ? 0xff6b6b : 0x4ecdc4;

      zoneGraphics.fillStyle(color, 0.15);
      zoneGraphics.fillCircle(screenPos.x, screenPos.y, 60);
      zoneGraphics.lineStyle(2, color, 0.8);
      zoneGraphics.strokeCircle(screenPos.x, screenPos.y, 60);

      // 电荷量标签
      this.add.text(screenPos.x, screenPos.y - 75,
        source.charge > 0 ? `+${source.charge}C` : `${source.charge}C`,
        {
          fontSize: '14px',
          color: source.charge > 0 ? '#ff6b6b' : '#4ecdc4',
          fontFamily: 'Arial',
        }
      ).setOrigin(0.5);
    }

    // 磁场区域标记
    const magneticSources = this.levelConfig?.magneticSources ?? [];
    for (const source of magneticSources) {
      const screenPos = this.toScreenCoords({ x: source.position.x, y: source.position.y });
      const radius = source.radius ?? 100;
      const color = source.strength > 0 ? 0x9b59b6 : 0x3498db;

      // 磁场影响范围
      zoneGraphics.fillStyle(color, 0.1);
      zoneGraphics.fillCircle(screenPos.x, screenPos.y, radius);
      zoneGraphics.lineStyle(3, color, 0.6);
      zoneGraphics.strokeCircle(screenPos.x, screenPos.y, radius);

      // 磁场强度标签
      this.add.text(screenPos.x, screenPos.y,
        source.strength > 0 ? 'N' : 'S',
        {
          fontSize: '16px',
          color: '#ffffff',
          fontFamily: 'Arial',
          fontStyle: 'bold',
        }
      ).setOrigin(0.5);

      this.add.text(screenPos.x, screenPos.y + radius + 10,
        `B=${Math.abs(source.strength)}T`,
        {
          fontSize: '12px',
          color: '#9b59b6',
          fontFamily: 'Arial',
        }
      ).setOrigin(0.5);
    }
  }

  private createCheckpoints(): void {
    for (const checkpoint of this.checkpoints) {
      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const color = checkpoint.color ?? CHECKPOINT_COLOR;

      const graphics = this.add.graphics();
      graphics.fillStyle(color, 0.3);
      graphics.fillRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);
      graphics.lineStyle(3, color);
      graphics.strokeRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);

      this.checkpointGraphics.set(checkpoint.id, graphics);

      const stageLabel = this.stages.find(s => s.stage === checkpoint.stage);
      this.add.text(screenX + checkpoint.size.width / 2, screenY - 15,
        `阶段${checkpoint.stage}: ${stageLabel?.name ?? ''}`,
        {
          fontSize: '12px',
          color: '#ffa500',
          fontFamily: 'Arial',
        }
      ).setOrigin(0.5);
    }
  }

  private createGoalZone(): void {
    const goal = this.levelConfig?.goal;
    if (!goal) return;

    const screenX = goal.position.x - goal.size.width / 2;
    const screenY = GAME_HEIGHT - goal.position.y - goal.size.height / 2;

    this.goalZone = new Phaser.Geom.Rectangle(
      screenX,
      screenY,
      goal.size.width,
      goal.size.height
    );

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(GOAL_COLOR, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, GOAL_COLOR);
    goalGraphics.strokeRectShape(this.goalZone);

    // 终点脉冲动画
    this.time.addEvent({
      delay: 500,
      callback: () => {
        goalGraphics.clear();
        const pulseScale = 1 + Math.sin(this.elapsedTime * 2) * 0.1;
        goalGraphics.fillStyle(GOAL_COLOR, 0.5 * pulseScale);
        goalGraphics.fillRectShape(this.goalZone);
        goalGraphics.lineStyle(3, GOAL_COLOR);
        goalGraphics.strokeRectShape(this.goalZone);
      },
      loop: true,
    });

    this.add.text(this.goalZone.centerX, this.goalZone.y - 15, '终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    const startY = playerConfig?.startPosition.y ?? 300;
    const startX = playerConfig?.startPosition.x ?? 100;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: 8,
        jumpForce: 150,
        colors: {
          line: 0x00ff00,
          headFill: 0x1a1a2e,
          lineWidth: 3,
        },
      },
      GAME_HEIGHT
    );
  }

  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '电磁挑战 - 关卡10', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 阶段标题
    this.stageText = this.add.text(GAME_WIDTH / 2, 60, '', {
      fontSize: '18px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 85, '', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 进度文本
    this.progressText = this.add.text(10, 10, '进度: 阶段1/3', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 公式展示
    this.formulaText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 30, '', {
      fontSize: '16px',
      color: '#00ff00',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5);

    this.createStageIndicators();
    this.createPlayerChargeIndicator();
    this.updateStageDisplay();
  }

  /**
   * 创建玩家电荷指示器
   */
  private createPlayerChargeIndicator(): void {
    const chargeColor = this.playerCharge > 0 ? '#ff6b6b' : '#4ecdc4';
    const chargeLabel = this.playerCharge > 0 ? '正电荷' : '负电荷';

    this.add.text(10, 35, `玩家: ${chargeLabel} (${this.playerCharge}C)`, {
      fontSize: '14px',
      color: chargeColor,
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });
  }

  private createStageIndicators(): void {
    const startX = GAME_WIDTH - 220;
    const y = 10;
    const width = 70;
    const gap = 10;

    for (let i = 1; i <= 3; i++) {
      const x = startX + (i - 1) * (width + gap);
      const stageConfig = this.stages.find(s => s.stage === i);

      const bg = this.add.graphics();
      bg.fillStyle(STAGE_COLORS[i as CurrentStage], 0.3);
      bg.fillRect(x, y, width, 30);
      bg.lineStyle(2, STAGE_COLORS[i as CurrentStage]);
      bg.strokeRect(x, y, width, 30);

      this.add.text(x + width / 2, y + 15, stageConfig?.name ?? `阶段${i}`, {
        fontSize: '10px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private setupInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        this.resetLevel();
      }
      // 切换电荷符号（按 C 键）
      if (event.key === 'c' || event.key === 'C') {
        this.togglePlayerCharge();
      }
    });
  }

  /**
   * 切换玩家电荷符号
   */
  private togglePlayerCharge(): void {
    this.playerCharge = -this.playerCharge;
    const chargeColor = this.playerCharge > 0 ? 0xff6b6b : 0x4ecdc4;
    this.stickman.setColors({
      line: chargeColor,
      headFill: 0x1a1a2e,
      lineWidth: 3,
    });
  }

  private checkCheckpointCollisions(): void {
    const headPos = this.stickman.getHeadPosition();

    for (const checkpoint of this.checkpoints) {
      if (this.completedStages.has(checkpoint.stage ?? 0)) continue;

      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const checkpointRect = new Phaser.Geom.Rectangle(
        screenX,
        screenY,
        checkpoint.size.width,
        checkpoint.size.height
      );

      if (checkpointRect.contains(headPos.x, headPos.y)) {
        this.completeStage(checkpoint.stage ?? 1);
      }
    }
  }

  private completeStage(stage: number): void {
    if (this.completedStages.has(stage)) return;

    this.completedStages.add(stage);

    const graphics = this.checkpointGraphics.get(`checkpoint-${stage}`);
    if (graphics) {
      graphics.clear();
      graphics.fillStyle(0x4ade80, 0.5);
      graphics.fillRect(
        graphics.x ?? 0,
        graphics.y ?? 0,
        this.checkpoints.find(c => c.stage === stage)?.size.width ?? 60,
        this.checkpoints.find(c => c.stage === stage)?.size.height ?? 80
      );
    }

    this.showStageTransition(stage);

    if (stage < 3) {
      this.currentStage = (stage + 1) as CurrentStage;
    }
  }

  private showStageTransition(stage: number): void {
    const stageConfig = this.stages.find(s => s.stage === stage);
    const transitionBg = this.add.graphics();
    transitionBg.fillStyle(0x000000, 0.7);
    transitionBg.fillRect(GAME_WIDTH / 2 - 150, GAME_HEIGHT / 2 - 60, 300, 120);

    const stageColor = STAGE_COLORS[stage as CurrentStage];
    const colorHex = `#${stageColor.toString(16).padStart(6, '0')}`;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, `阶段${stage} 完成！`, {
      fontSize: '24px',
      color: colorHex,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    if (stageConfig) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, stageConfig.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    this.time.delayedCall(1500, () => {
      transitionBg.destroy();
    });
  }

  private updateStageDisplay(): void {
    const stageConfig = this.stages.find(s => s.stage === this.currentStage);
    if (stageConfig) {
      this.stageText.setText(`当前: ${stageConfig.name}`);
      const stageColor = STAGE_COLORS[this.currentStage];
      this.stageText.setColor(`#${stageColor.toString(16).padStart(6, '0')}`);
      this.hintText.setText(stageConfig.hint ?? '');
    }
    this.progressText.setText(`进度: 阶段${this.currentStage}/3 (${this.completedStages.size}/3 完成)`);
    this.updateFormulaDisplay();
  }

  /**
   * 更新公式显示（根据当前阶段）
   */
  private updateFormulaDisplay(): void {
    let formula = '';
    switch (this.currentStage) {
      case 1:
        formula = '电场力: F = qE';
        break;
      case 2:
        formula = '洛伦兹力: F = qv×B';
        break;
      case 3:
        formula = '电磁感应: ε = -dΦ/dt';
        break;
    }
    this.formulaText.setText(formula);
  }

  private checkVictory(): void {
    const headPos = this.stickman.getHeadPosition();

    if (this.goalZone.contains(headPos.x, headPos.y) && this.completedStages.size >= 2) {
      this.isVictory = true;
      this.gameState = 'success';
      this.showVictoryMessage();
    }
  }

  private showVictoryMessage(): void {
    const saveGameState = getGameState();
    saveGameState.completeLevel(10, this.elapsedTime);

    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.8);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80, '🎉 电磁挑战完成！', {
      fontSize: '36px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 30,
      '你已掌握了电场力、磁场力与洛伦兹力的基本原理！',
      {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 10,
      '电场力 F = qE | 洛伦兹力 F = qv×B | 电磁感应 ε = -dΦ/dt',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    const backBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 60, '返回', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 60, '重新挑战', {
      fontSize: '20px',
      color: '#ffd700',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetLevel();
    });
  }

  private resetLevel(): void {
    this.scene.restart();
  }

  /**
   * 物理坐标转屏幕坐标
   */
  private toScreenCoords(pos: { x: number; y: number }): { x: number; y: number } {
    return {
      x: pos.x,
      y: GAME_HEIGHT - pos.y,
    };
  }

  getCurrentStage(): CurrentStage {
    return this.currentStage;
  }

  getCompletedStages(): Set<number> {
    return this.completedStages;
  }

  getIsVictory(): boolean {
    return this.isVictory;
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getGameState(): GameState {
    return this.gameState;
  }

  getEMField(): EMField {
    return this.emField;
  }

  getPlayerCharge(): number {
    return this.playerCharge;
  }
}